import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { applyUserTheme, getStoredUserSettings } from '../utils/userSettings';

const AuthContext = createContext();
const DISABLE_AUTH = process.env.REACT_APP_DISABLE_AUTH === 'true';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(
    DISABLE_AUTH
      ? {
          id: 0,
          email: 'dev@hiringcrm.local',
          name: 'Dev User',
          isSuperAdmin: true
        }
      : null
  );
  const [loading, setLoading] = useState(DISABLE_AUTH ? false : true);

  useEffect(() => {
    if (DISABLE_AUTH) {
      // In dev mode with auth disabled, skip token fetching entirely
      applyUserTheme(getStoredUserSettings(0));
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    applyUserTheme(getStoredUserSettings(user?.id));
  }, [user]);

  const fetchUser = async () => {
    if (DISABLE_AUTH) return;

    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const applyAuthenticatedSession = (token, nextUser) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(nextUser);
  };

  const login = async (email, password) => {
    if (DISABLE_AUTH) {
      // In dev mode, pretend login always succeeds and set a fake user
      const devUser = {
        id: 0,
        email: email || 'dev@hiringcrm.local',
        name: 'Dev User',
        isSuperAdmin: true
      };
      setUser(devUser);
      return { success: true };
    }

    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token, user } = response.data;
      applyAuthenticatedSession(token, user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await axios.post(`/api/auth/impersonate/${userId}`);
      const { token, user: nextUser } = response.data;
      applyAuthenticatedSession(token, nextUser);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to impersonate user'
      };
    }
  };

  const exitImpersonation = async () => {
    try {
      const response = await axios.post('/api/auth/impersonate/exit');
      const { token, user: nextUser } = response.data;
      applyAuthenticatedSession(token, nextUser);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to exit impersonation'
      };
    }
  };

  const logout = () => {
    if (DISABLE_AUTH) {
      // In dev mode, keep the fake user logged in
      return;
    }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const hasPermission = (resource, action) => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    return user.permissions && user.permissions.includes(`${resource}:${action}`);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    fetchUser,
    hasPermission,
    impersonateUser,
    exitImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
